// Phase IX.F.6 · What's New ⌘⇧K palette · V12 Achromatic Strict.
// Trigger: ⌘⇧K / Ctrl+Shift+K globale (⌘K è già occupato dal command palette esistente).
// Persist dismiss: localStorage + backend `/v2/user-prefs/dismiss`.
import React, { useEffect, useState, useCallback } from "react";
import { Sparkles, X, ArrowRight, Boxes, LineChart, Trophy } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { API } from "@/lib/api";

const FEATURE_ID = "whatsnew_phase_ix";
const STORAGE_KEY = "whatsnew_phase_ix_dismissed_v1";

const WHATSNEW_FEATURES = [
    {
        id: "suites",
        title: "9 Marketplace Suites",
        description: "Bundle preconfigurati · asset + agent + ecosystem in un click.",
        icon: Boxes,
        href: "/app/suites",
        roles: ["user", "admin", "super_admin"],
    },
    {
        id: "trigger-log",
        title: "Automations Trigger Log",
        description: "Monitor real-time dei trigger reali del sistema.",
        icon: LineChart,
        href: "/app/automations",
        roles: ["admin", "super_admin"],
    },
    {
        id: "leaderboard",
        title: "Public Leaderboard",
        description: "Classifica SEO-indexed con profili pubblici.",
        icon: Trophy,
        href: "/leaderboard",
        roles: ["user", "admin", "super_admin"],
    },
];

export default function WhatsNewPalette() {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [dismissed, setDismissed] = useState(true);
    const [showTooltip, setShowTooltip] = useState(false);

    // Boot · check dismiss state (localStorage first, backend fallback)
    useEffect(() => {
        if (!user) return;
        const local = localStorage.getItem(STORAGE_KEY);
        if (local === "true") { setDismissed(true); return; }
        (async () => {
            try {
                const r = await fetch(`${API}/v2/user-prefs/me`, { credentials: "include" });
                if (r.ok) {
                    const d = await r.json();
                    if (d.dismissed && d.dismissed[FEATURE_ID]) {
                        setDismissed(true);
                        localStorage.setItem(STORAGE_KEY, "true");
                        return;
                    }
                }
            } catch {}
            // Not dismissed · show first-login tooltip after 1.5s
            setDismissed(false);
            const t = setTimeout(() => setShowTooltip(true), 1500);
            return () => clearTimeout(t);
        })();
    }, [user]);

    // Hotkey ⌘⇧K / Ctrl+Shift+K
    useEffect(() => {
        function onKey(e) {
            if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === "k") {
                e.preventDefault();
                setOpen((v) => !v);
                setShowTooltip(false);
            }
            if (e.key === "Escape") setOpen(false);
        }
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    const handleDismiss = useCallback(async () => {
        setDismissed(true);
        setOpen(false);
        setShowTooltip(false);
        localStorage.setItem(STORAGE_KEY, "true");
        try {
            await fetch(`${API}/v2/user-prefs/dismiss`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ feature_id: FEATURE_ID }),
            });
        } catch {}
    }, []);

    if (!user) return null;

    const role = (user.role || "user").toLowerCase();
    const visible = WHATSNEW_FEATURES.filter((f) => f.roles.includes(role));

    return (
        <>
            {/* First-login tooltip corner-bottom-right */}
            {showTooltip && !open && !dismissed && (
                <div
                    data-testid="whatsnew-tooltip"
                    className="fixed bottom-24 right-6 z-40 max-w-[260px] rounded-md border v12-border v12-card p-3 shadow-lg"
                >
                    <div className="flex items-start justify-between gap-2">
                        <div>
                            <p className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1">
                                Novità
                            </p>
                            <p className="text-xs text-neutral-200">
                                Scopri le 3 nuove feature · premi
                                {" "}<kbd className="font-mono text-[10px] px-1 py-0.5 bg-neutral-800 border v12-border rounded">⌘⇧K</kbd>
                            </p>
                        </div>
                        <button
                            onClick={() => setShowTooltip(false)}
                            data-testid="whatsnew-tooltip-close"
                            className="text-neutral-500 hover:text-neutral-200"
                        >
                            <X size={13} />
                        </button>
                    </div>
                    <button
                        onClick={() => { setOpen(true); setShowTooltip(false); }}
                        data-testid="whatsnew-tooltip-open"
                        className="mt-2 text-[11px] px-2.5 py-1 rounded-md border v12-border bg-neutral-800 text-neutral-200 hover:bg-neutral-700"
                    >
                        Apri What's New
                    </button>
                </div>
            )}

            {/* Palette overlay */}
            {open && (
                <div
                    className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 pt-20"
                    onClick={() => setOpen(false)}
                    data-testid="whatsnew-palette-overlay"
                >
                    <div
                        className="w-full max-w-xl rounded-md border v12-border v12-card shadow-xl"
                        onClick={(e) => e.stopPropagation()}
                        data-testid="whatsnew-palette"
                    >
                        <div className="flex items-center justify-between px-5 py-4 border-b v12-border">
                            <div className="flex items-center gap-2">
                                <Sparkles size={16} className="stroke-zinc-400" />
                                <h2 className="text-sm font-semibold text-neutral-100">
                                    What's New · Phase IX
                                </h2>
                            </div>
                            <button
                                onClick={() => setOpen(false)}
                                data-testid="whatsnew-palette-close"
                                className="text-neutral-500 hover:text-neutral-200"
                            >
                                <X size={14} />
                            </button>
                        </div>

                        <div className="p-4 space-y-2" data-testid="whatsnew-palette-list">
                            {visible.map((f) => {
                                const Icon = f.icon;
                                return (
                                    <Link
                                        key={f.id}
                                        to={f.href}
                                        onClick={() => setOpen(false)}
                                        data-testid={`whatsnew-item-${f.id}`}
                                        className="flex items-center gap-3 px-3 py-3 rounded-md border v12-border bg-neutral-900 hover:bg-neutral-800"
                                    >
                                        <Icon size={16} className="stroke-zinc-400 shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs font-medium text-neutral-100">{f.title}</p>
                                            <p className="text-[11px] text-neutral-500">{f.description}</p>
                                        </div>
                                        <ArrowRight size={13} className="stroke-zinc-500 shrink-0" />
                                    </Link>
                                );
                            })}
                        </div>

                        <div className="px-5 py-3 border-t v12-border flex items-center justify-between">
                            <p className="text-[10px] text-neutral-600">
                                <kbd className="font-mono text-[10px] px-1 py-0.5 bg-neutral-800 border v12-border rounded">⌘⇧K</kbd>
                                {" "}per riaprire · Esc per chiudere
                            </p>
                            <button
                                onClick={handleDismiss}
                                data-testid="whatsnew-palette-dismiss"
                                className="text-[11px] px-2.5 py-1 rounded-md border v12-border text-neutral-500 hover:text-neutral-200"
                            >
                                Non mostrare più
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
